import * as Blockly from 'blockly/core';
import { blockIcon } from '@/(routers)/boards/editor/block/blockFactory';
import { defineMutableBlock } from '@/(routers)/boards/editor/block/blockRegistry';
import { CATEGORIES } from '@/(routers)/boards/editor/block/definitions/categories';
import { FieldOperatorDropdown } from '@/(routers)/boards/editor/block/definitions/fields/fieldOperatorDropdown';
import { FieldSegmentedToggle } from '@/(routers)/boards/editor/block/definitions/fields/fieldSegmentedToggle';
import { FieldStyledLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldStyledLabel';

const COLOR = CATEGORIES.serial.color;
const ICON_ADD = { src: '/images/block/btn_add.svg', size: 20 };
const ICON_REMOVE = { src: '/images/block/btn_remove.svg', size: 20 };
const ICON_MONITOR = '/images/block/monitor_print.svg';
const ICON_MONITOR_SEP = '/images/block/monitor_print_sep.svg';

function getIds(block: Blockly.Block): string[] {
    return (block as any)._ids as string[];
}

function getTypes(block: Blockly.Block): Record<string, 'number' | 'string'> {
    return (block as any)._types as Record<string, 'number' | 'string'>;
}

function getNewlineValue(block: Blockly.Block): string {
    return (block as any)._newlineValue ?? block.getFieldValue('NEWLINE') ?? 'ALL';
}

function buildNewlineDropdown(): FieldOperatorDropdown {
    return new FieldOperatorDropdown([
        { value: 'ALL', label: 'あり' },
        { value: 'LAST', label: '最終行のみ' },
        { value: 'NO', label: 'なし' },
    ]);
}

function switchNewlineToLabel(block: Blockly.Block): void {
    const input = block.getInput('HEADER');
    if (!input) return;
    const currentField = block.getField('NEWLINE');
    if (!currentField || currentField instanceof FieldStyledLabel) return;
    const idx = input.fieldRow.findIndex((f: any) => f.name === 'NEWLINE');
    input.removeField('NEWLINE');
    input.insertFieldAt(
        idx >= 0 ? idx : input.fieldRow.length,
        new FieldStyledLabel('最終行のみ', { fontSize: 13 }),
        'NEWLINE',
    );
    (block as any)._newlineValue = 'LAST';
}

function switchNewlineToDropdown(block: Blockly.Block): void {
    const input = block.getInput('HEADER');
    if (!input) return;
    const currentField = block.getField('NEWLINE');
    if (!currentField || !(currentField instanceof FieldStyledLabel)) return;
    const idx = input.fieldRow.findIndex((f: any) => f.name === 'NEWLINE');
    input.removeField('NEWLINE');
    input.insertFieldAt(idx >= 0 ? idx : input.fieldRow.length, buildNewlineDropdown(), 'NEWLINE');
    delete (block as any)._newlineValue;
}

function appendHeader(block: Blockly.Block): void {
    block
        .appendDummyInput('HEADER')
        .appendField(blockIcon({ src: ICON_MONITOR, size: 70 }), 'ICON')
        .appendField(new FieldStyledLabel('シリアルモニター', { fontSize: 15, fontWeight: 'bold' }))
        .appendField(' 区切り文字は')
        .appendField(
            new FieldOperatorDropdown(
                [
                    { value: 'NONE', label: 'なし' },
                    { value: 'COMMA', label: 'カンマ' },
                    { value: 'SPACE', label: 'スペース' },
                    { value: 'TAB', label: 'タブ' },
                ],
                function (this: FieldOperatorDropdown, value: string) {
                    const b = this.getSourceBlock();
                    if (!b) return value;
                    requestAnimationFrame(() => {
                        const iconField = b.getField('ICON') as Blockly.FieldImage | null;
                        iconField?.setValue(value === 'NONE' ? ICON_MONITOR : ICON_MONITOR_SEP);
                        b.setColour(value === 'NONE' ? COLOR : '#28E46D');

                        if (value !== 'NONE') {
                            switchNewlineToLabel(b);
                        } else {
                            switchNewlineToDropdown(b);
                        }

                        (b.getField('SEPARATOR') as any)?.refreshStyle?.();
                        (b.getField('NEWLINE') as any)?.refreshStyle?.();
                        (b as Blockly.BlockSvg).render();
                    });
                    return value;
                },
            ),
            'SEPARATOR',
        )
        .appendField('で、改行は')
        .appendField(buildNewlineDropdown(), 'NEWLINE')
        .appendField('で')
        .appendField(
            new Blockly.FieldImage(ICON_ADD.src, ICON_ADD.size, ICON_ADD.size, '+', function () {
                addValue(block);
            }),
            'ADD',
        )
        .appendField(
            new Blockly.FieldImage(ICON_REMOVE.src, ICON_REMOVE.size, ICON_REMOVE.size, '-', function () {
                removeLastValue(block);
            }),
            'REMOVE',
        );
}

function refreshFooter(block: Blockly.Block): void {
    if (block.getInput('FOOTER')) block.removeInput('FOOTER');
    block.appendValueInput('FOOTER').setCheck('__none__').appendField('を出力する');
}

function attachShadow(block: Blockly.Block, inputName: string, type: 'number' | 'string'): void {
    const input = block.getInput(inputName);
    if (!input?.connection || input.connection.targetBlock()) return;

    const shadow = (block.workspace as Blockly.WorkspaceSvg).newBlock(type);
    shadow.setShadow(true);
    shadow.outputConnection?.connect(input.connection);

    requestAnimationFrame(() => {
        shadow.initSvg();
        shadow.render();
    });
}

function switchType(block: Blockly.Block, id: string, type: 'number' | 'string'): void {
    const types = getTypes(block);
    types[id] = type;

    const labelField = block.getField(`TYPE_${id}`) as FieldStyledLabel | null;
    labelField?.setValue(type === 'number' ? '数値' : '文字列');

    const input = block.getInput(`VAL_${id}`);
    if (!input?.connection) return;

    Blockly.Events.disable();
    try {
        const existing = input.connection.targetBlock() as Blockly.BlockSvg | null;
        if (existing) {
            input.connection.disconnect();
            existing.getSvgRoot()?.remove();
            existing.dispose(false);
        }

        input.connection.setShadowDom(null);
        (input.connection as any).shadowState_ = null;

        input.connection.setCheck(type === 'number' ? 'Number' : 'String');

        const ws = block.workspace as Blockly.WorkspaceSvg;
        const shadow = ws.newBlock(type) as Blockly.BlockSvg;
        shadow.setShadow(true);
        shadow.initSvg();
        shadow.outputConnection?.connect(input.connection);
        shadow.render();
        (block as Blockly.BlockSvg).render();
        ws.render();
    } finally {
        Blockly.Events.enable();
    }
}

function appendValueRow(block: Blockly.Block, id: string): void {
    const type = getTypes(block)[id] ?? 'number';

    block
        .appendValueInput(`VAL_${id}`)
        .setCheck(type === 'number' ? 'Number' : 'String')
        .appendField(
            new FieldSegmentedToggle(type, {
                options: [
                    { value: 'number', label: '数値' },
                    { value: 'string', label: '文字列' },
                ],
                activeColor: '#FFFFFF',
                onChange: (next: string) => {
                    switchType(block, id, next as 'number' | 'string');
                },
            }),
            `TYPE_${id}`,
        );

    attachShadow(block, `VAL_${id}`, type);
}

function syncCode(block: Blockly.Block): void {
    requestAnimationFrame(() => {
        const { generateArduinoCode } = require('@/(routers)/boards/editor/block/generators/arduino');
        const { useBoardEditorFileStore } = require('@/stores/useBoardEditorFileStore');
        const { useBoardEditorStore } = require('@/stores/useBoardEditorStore');
        const ws = block.workspace as Blockly.WorkspaceSvg;
        const { currentFileId, updateFile, markAsDirty } = useBoardEditorFileStore.getState();
        if (!currentFileId) return;
        const xml = Blockly.Xml.workspaceToDom(ws);
        const xmlText = Blockly.Xml.domToText(xml);
        const generatedCode = generateArduinoCode(ws);
        updateFile(currentFileId, { blockContent: xmlText, codeContent: generatedCode });
        useBoardEditorStore.getState().setCode(generatedCode);
        markAsDirty(currentFileId);
    });
}

function addValue(block: Blockly.Block): void {
    Blockly.Events.disable();
    try {
        const ids = getIds(block);
        const newId = Math.random().toString(36).slice(2, 8);
        ids.push(newId);
        getTypes(block)[newId] = 'number';
        appendValueRow(block, newId);
    } finally {
        Blockly.Events.enable();
        refreshFooter(block);
        (block.workspace as Blockly.WorkspaceSvg).render();
    }
    syncCode(block);
}

function removeLastValue(block: Blockly.Block): void {
    const ids = getIds(block);
    if (ids.length <= 1) return;

    const lastId = ids[ids.length - 1];

    Blockly.Events.disable();
    try {
        const target = block.getInput(`VAL_${lastId}`)?.connection?.targetBlock();
        if (target && !target.isShadow()) target.dispose(false);

        if (block.getInput(`VAL_${lastId}`)) block.removeInput(`VAL_${lastId}`);

        delete getTypes(block)[lastId];
        ids.pop();
    } finally {
        Blockly.Events.enable();
        refreshFooter(block);
        (block.workspace as Blockly.WorkspaceSvg).render();
    }
    syncCode(block);
}

defineMutableBlock({
    type: 'monitor_print',
    colour: COLOR,
    tooltip: 'シリアルモニターに値を送信します',
    movable: true,
    deletable: true,
    previousStatement: true,
    nextStatement: true,

    init(this: Blockly.Block) {
        (this as any)._ids = [] as string[];
        (this as any)._types = {} as Record<string, 'number' | 'string'>;
        this.setColour(COLOR);
        this.setTooltip('シリアルモニターに値を送信します');
        this.setMovable(true);
        this.setDeletable(true);
        this.setPreviousStatement(true);
        this.setNextStatement(true);

        const firstId = Math.random().toString(36).slice(2, 8);
        (this as any)._ids.push(firstId);
        (this as any)._types[firstId] = 'number';

        appendHeader(this);
        appendValueRow(this, firstId);
        refreshFooter(this);
    },

    mutationToDom(this: Blockly.Block): Element {
        const container = Blockly.utils.xml.createElement('mutation');
        container.setAttribute('ids', getIds(this).join(','));
        const types = getTypes(this);
        container.setAttribute(
            'types',
            getIds(this)
                .map((id) => types[id] ?? 'number')
                .join(','),
        );
        container.setAttribute('separator', this.getFieldValue('SEPARATOR') ?? 'NONE');
        container.setAttribute('newline', getNewlineValue(this));
        return container;
    },

    domToMutation(this: Blockly.Block, xml: Element): void {
        for (const id of getIds(this)) {
            if (this.getInput(`VAL_${id}`)) this.removeInput(`VAL_${id}`);
        }
        if (this.getInput('FOOTER')) this.removeInput('FOOTER');

        const raw = xml.getAttribute('ids') ?? '';
        const ids = raw ? raw.split(',').filter(Boolean) : [];
        const rawTypes = xml.getAttribute('types') ?? '';
        const typeList = rawTypes ? rawTypes.split(',') : [];
        const separator = xml.getAttribute('separator') ?? 'NONE';
        const newline = xml.getAttribute('newline') ?? 'ALL';

        if (ids.length === 0) {
            ids.push(Math.random().toString(36).slice(2, 8));
        }

        (this as any)._ids = ids;
        (this as any)._types = {} as Record<string, 'number' | 'string'>;
        ids.forEach((id, i) => {
            (this as any)._types[id] = (typeList[i] === 'string' ? 'string' : 'number') as 'number' | 'string';
        });

        if (!this.getInput('HEADER')) {
            appendHeader(this);
        }

        requestAnimationFrame(() => {
            const sepField = this.getField('SEPARATOR') as FieldOperatorDropdown | null;
            sepField?.setValue(separator);

            if (separator !== 'NONE') {
                this.setColour('#28E46D');
                const iconField = this.getField('ICON') as Blockly.FieldImage | null;
                iconField?.setValue(ICON_MONITOR_SEP);
                switchNewlineToLabel(this);
            } else {
                const newlineField = this.getField('NEWLINE') as FieldOperatorDropdown | null;
                newlineField?.setValue(newline);
            }
        });

        ids.forEach((id) => appendValueRow(this, id));
        refreshFooter(this);
    },
});
